import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { PlusOutlined, LeftOutlined, RightOutlined, MoreOutlined } from "@ant-design/icons";
import { Dropdown } from "antd";
import type { MenuProps } from "antd";
import { BlockData, BlockType, BlockStatus } from "@/api/block";
import { useBlockTree } from "../../hooks/useBlockTree";
import { getCountryShort } from "@/shared/utils/formatters";
import { AppSelect, Icon, type IconName } from "@/shared/components";
import { CUSTOM_COLORS } from "@/shared/theme/colorUtils";
import { canToggleDeliverableCompletion } from "../../utils/deliverableCompletionPolicy";

// ─── Types ───────────────────────────────────────────────────────

interface VettingChainProps {
	blocks: BlockData[];
	onBlockClick?: (blockId: string) => void;
	onAddNode?: (parentId: string) => void;
	onRemoveNode?: (nodeId: string) => void;
	onUpdateNode?: (nodeId: string, data: Partial<BlockData>) => void;
	onMarkComplete?: (blockId: string) => void;
	onRename?: (blockId: string) => void;
	onDelete?: (blockId: string) => void;
	canManageDeliverables?: boolean;
	verifiedDeliverableIds?: ReadonlySet<string>;
	immutableDeliverableIds?: ReadonlySet<string>;
	completionStatusResolved?: boolean;
	mode?: "view" | "edit";
}

// These types should always appear together in one column, never drill into separate columns
const SAME_COLUMN_TYPES = new Set([BlockType.CREATOR_LIST, BlockType.CREATOR_OUTREACH, BlockType.CREATOR_GROUPING]);

const CLICKABLE_TYPES: BlockType[] = [
	BlockType.CREATOR,
	BlockType.DELIVERABLE,
	BlockType.CONCEPT,
	BlockType.VIDEO,
	BlockType.LINK,
	BlockType.CREATOR_LIST,
	BlockType.CREATOR_OUTREACH,
	BlockType.CREATOR_GROUPING,
];

const ADDABLE_PARENT_TYPES = new Set([BlockType.CREATOR, BlockType.DELIVERABLE, BlockType.CONCEPT, BlockType.VIDEO]);

const EDITABLE_TYPES = new Set([BlockType.DELIVERABLE, BlockType.CONCEPT, BlockType.VIDEO, BlockType.LINK]);

// Map non-standard short codes to ISO 3166-1 alpha-2 for flag icons
const SHORT_TO_ISO: Record<string, string> = {
	CBDA: "KH",
	GCC: "",
	KAZ: "KZ",
	KSA: "SA",
	RSA: "ZA",
	SP: "ES",
	UAE: "AE",
	UK: "GB",
	USA: "US",
};

function getFlagUrl(countryName: string): string {
	const short = getCountryShort(countryName);
	const iso = SHORT_TO_ISO[short] ?? short;
	if (!iso || iso.length !== 2) return "";
	return `https://purecatamphetamine.github.io/country-flag-icons/3x2/${iso}.svg`;
}

const COLUMN_MIN_WIDTH = 160;
const MOBILE_BREAKPOINT = 768;

// ─── Styled Components ──────────────────────────────────────────

const ScrollSentinel = styled.div`
	height: 1px;
	width: 100%;
	margin-bottom: -1px;
`;

const Wrapper = styled.div<{ $collapsed: boolean }>`
	width: 100%;
	overflow: ${(props) => (props.$collapsed ? "visible" : "hidden")};
	background: ${(props) =>
		props.$collapsed ? "linear-gradient(to bottom, #f5f5f5 60%, transparent)" : "transparent"};
	padding-bottom: ${(props) => (props.$collapsed ? "30px" : "0")};
	border-radius: 12px;
	box-shadow: 0px 4px 12px 0px #1559a51f;
`;

const FullContainer = styled.div<{ $collapsed: boolean }>`
	width: 100%;
	background: #fff;
	border-radius: 12px;
	border: 1px solid #afb7d9;
	overflow: hidden;
	border-width: ${(props) => (props.$collapsed ? "0" : "1px")};
	opacity: ${(props) => (props.$collapsed ? 0 : 1)};
	height: ${(props) => (props.$collapsed ? "0" : "auto")};
	transition:
		border-width 0.3s ease,
		opacity 0.25s ease;
`;

const ChainLayout = styled.div<{ $collapsed: boolean; $height: number }>`
	display: ${(props) => (props.$collapsed ? "none" : "flex")};
	height: ${(props) => props.$height}px;
	max-height: ${(props) => props.$height}px;
	overflow: hidden;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		display: none;
	}
`;

// ─── Market Pills (rectangular with flag + name + chevron) ──────

const MarketColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	min-width: 80px;
	flex-shrink: 0;
	background: #fafbff;
	padding: 16px;
	border-right: 1px solid #afb7d9;
	overflow-y: auto;

	&::-webkit-scrollbar {
		width: 3px;
	}
	&::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 2px;
	}
`;

const MarketPill = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 14px;
	height: 52px;
	min-height: 52px;
	max-height: 52px;
	border-radius: 12px;
	border: 1px solid ${(props) => (props.$active ? "#F72586" : "#e8e8e8")};
	background: ${(props) => (props.$active ? "#FFF0F6" : "#fff")};
	cursor: pointer;
	font-size: 13px;
	font-weight: 600;
	color: ${(props) => (props.$active ? "#F72586" : "#333")};
	transition: all 0.15s ease;
	white-space: nowrap;

	&:hover {
		border-color: #f72586;
		background: #fff0f6;
		color: #f72586;
	}
`;

const MarketFlag = styled.img`
	width: 20px;
	height: 20px;
	border-radius: 50%;
	object-fit: cover;
`;

const MarketChevron = styled(RightOutlined)`
	font-size: 10px;
	margin-left: auto;
	color: inherit;
`;

// ─── Columns ────────────────────────────────────────────────────

const ColumnsSection = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	min-width: 0;
	overflow: hidden;
`;

const ColumnsScroll = styled.div`
	display: flex;
	overflow-x: auto;
	flex: 1;
	background: #eff1f9;
	padding: 16px 0;
	min-height: 0;

	&::-webkit-scrollbar {
		height: 4px;
	}
	&::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 2px;
	}
`;

const Column = styled.div`
	width: 16.5%;
	min-width: ${COLUMN_MIN_WIDTH}px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 0 12px;
	overflow-y: auto;
	border-left: 1px solid #afb7d9;

	&:first-child {
		border-left: none;
		margin-left: 0;
	}

	&::-webkit-scrollbar {
		width: 3px;
	}
	&::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 2px;
	}
`;

const AddBtn = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
	width: 100%;
	padding: 10px 14px;
	min-height: 40px;
	background: transparent;
	border: 1px dashed transparent;
	border-radius: 8px;
	color: #707070;
	font-size: 13px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.12s ease;

	&:hover {
		background: #fff0f6;
		border-color: #f72586;
		color: #f72586;
	}
`;

// ─── Block Items ────────────────────────────────────────────────
// "inPath" = selected in path chain (pink border)
// "isLeafActive" = the final active content block (pink fill)

const BlockCard = styled.button<{
	$inPath: boolean;
	$isLeafActive: boolean;
	$clickable: boolean;
}>`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 8px 14px;
	border-radius: 12px;
	text-align: left;
	font-size: 14px;
	font-weight: 400;
	height: 52px;
	min-height: 52px;
	max-height: 52px;
	cursor: ${(props) => (props.$clickable ? "pointer" : "default")};
	transition: all 0.12s ease;

	/* Leaf active = pink fill, path selected = pink border, default = card style */
	background: ${(props) => (props.$isLeafActive ? "#F72586" : "#fff")};
	color: ${(props) => (props.$isLeafActive ? "#fff" : "#333")};
	border: 1px solid ${(props) => (props.$isLeafActive ? "#F72586" : props.$inPath ? "#F72586" : "#e8e8e8")};
	box-shadow: ${(props) =>
		props.$isLeafActive
			? "0px 4px 12px 0px #F725863D"
			: props.$inPath
				? "0px 4px 12px 0px #F725863D"
				: "0 1px 3px rgba(0,0,0,0.06)"};

	&:hover {
		background: ${(props) => (props.$isLeafActive ? "#e01a6f" : props.$inPath ? "#FFF0F6" : "#fafafa")};
		border-color: ${(props) => (props.$isLeafActive || props.$inPath ? "#F72586" : "#d0d0d0")};
	}

	&:hover .trailing-chevron {
		display: none;
	}
	&:hover .trailing-more {
		display: block;
	}
`;

const BlockName = styled.span<{ $emphasized: boolean }>`
	flex: 1;
	display: block;
	font-weight: ${(props) => (props.$emphasized ? 600 : 500)};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	min-width: 0;
`;

const BlockText = styled.span`
	display: flex;
	min-width: 0;
	flex: 1;
	flex-direction: column;
`;

const BlockTitleRow = styled.span`
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
`;

const completionStatusIn = keyframes`
	from {
		opacity: 0;
		transform: translateY(-3px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const BlockStatusText = styled.span<{ $selected: boolean }>`
	overflow: hidden;
	padding-left: 24px;
	color: ${(props) => (props.$selected ? "#fff" : CUSTOM_COLORS.PRIMARY)};
	font-size: 11px;
	font-weight: 400;
	line-height: 16px;
	text-overflow: ellipsis;
	white-space: nowrap;
	animation: ${completionStatusIn} 180ms ease-out;

	@media (prefers-reduced-motion: reduce) {
		animation: none;
	}
`;

const VerifiedMark = styled.span`
	display: inline-flex;
	width: 16px;
	height: 16px;
	align-items: center;
	justify-content: center;
	border-radius: 50%;
	background: #bdf28d;
	flex-shrink: 0;
`;

const ContextAnchor = styled.div<{ $x: number; $y: number }>`
	position: fixed;
	top: ${(props) => props.$y}px;
	left: ${(props) => props.$x}px;
	width: 1px;
	height: 1px;
	z-index: 1100;

	.app-select-control {
		width: 1px;
		height: 1px;
		opacity: 0;
	}
`;

const ContextOption = styled.span<{ $danger?: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 12px;
	color: ${(props) => (props.$danger ? "#d30024" : "#303030")};
`;

const Chevron = styled(RightOutlined)<{ $light: boolean }>`
	font-size: 10px;
	color: ${(props) => (props.$light ? "#fff" : "#bbb")};
	flex-shrink: 0;
`;

const MoreDots = styled(MoreOutlined)<{ $light: boolean }>`
	font-size: 14px;
	color: ${(props) => (props.$light ? "#fff" : "#999")};
	flex-shrink: 0;
	cursor: pointer;
	padding: 2px;
	border-radius: 4px;
	transition: color 0.12s ease;

	&:hover {
		color: ${(props) => (props.$light ? "#fff" : "#333")};
	}
`;

const TrailingAction = styled.span`
	flex-shrink: 0;
	width: 16px;
	display: flex;
	align-items: center;
	justify-content: center;

	.trailing-chevron {
		display: block;
	}
	.trailing-more {
		display: none;
	}
`;

const BlockIcon = styled.span<{ $light: boolean }>`
	font-size: 12px;
	flex-shrink: 0;
	display: flex;
	align-items: center;

	img {
		filter: ${(props) => (props.$light ? "brightness(0) invert(1)" : "none")};
	}
`;

// ─── Breadcrumb ──────────────────────────────────────────────────

const BreadcrumbBar = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 10px 16px;
	border-top: 1px solid #afb7d9;
	background: #eff1f9;
	font-size: 13px;
	color: #666;
	overflow-x: auto;
	white-space: nowrap;

	&::-webkit-scrollbar {
		height: 3px;
	}
	&::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 2px;
	}
`;

const BreadcrumbSegment = styled.span<{ $isLast: boolean }>`
	display: inline-flex;
	align-items: center;
	gap: 4px;
	color: ${(props) => (props.$isLast ? "#F72586" : "#666")};
	font-weight: ${(props) => (props.$isLast ? 600 : 500)};
	flex-shrink: 0;
`;

const BreadcrumbIcon = styled(Icon)<{ $isLast: boolean }>`
	opacity: ${(props) => (props.$isLast ? 1 : 0.5)};
`;

const BreadcrumbSep = styled.span`
	color: #ccc;
	flex-shrink: 0;
`;

// ─── Mini (Sticky) Bar ──────────────────────────────────────────

const CollapsedWrap = styled.div<{ $visible: boolean }>`
	padding-bottom: 16px;
	margin-bottom: -16px;
	background: transparent;
	position: relative;
	z-index: 2;
	max-height: ${(props) => (props.$visible ? "60px" : "0")};
	opacity: ${(props) => (props.$visible ? 1 : 0)};
	transform: translateY(${(props) => (props.$visible ? "0" : "-8px")});
	pointer-events: ${(props) => (props.$visible ? "auto" : "none")};
	overflow: hidden;
	transition:
		max-height 0.25s ease,
		opacity 0.25s ease,
		transform 0.25s ease;
`;

const CollapsedBar = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 10px 16px;
	background: #eff1f9;
	border: 1px solid #afb7d9;
	border-radius: 12px;
	font-size: 13px;
	cursor: pointer;
	overflow-x: auto;
	white-space: nowrap;

	&::-webkit-scrollbar {
		height: 3px;
	}
	&::-webkit-scrollbar-thumb {
		background: #d9d9d9;
		border-radius: 2px;
	}

	&:hover {
		border-color: #f72586;
	}
`;

// ─── Mobile Layout ──────────────────────────────────────────────

const MobileContainer = styled.div`
	display: none;

	@media (max-width: ${MOBILE_BREAKPOINT}px) {
		display: flex;
		flex-direction: column;
	}
`;

const MobileMarketRow = styled.div`
	display: flex;
	gap: 12px;
	padding: 12px 16px;
	overflow-x: auto;
	border-bottom: 1px solid #f0f0f0;

	&::-webkit-scrollbar {
		height: 0;
	}
`;

const MobileMarketPill = styled.button<{ $active: boolean }>`
	width: 44px;
	height: 44px;
	border-radius: 50%;
	border: 1px solid ${(props) => (props.$active ? "#F72586" : "#e8e8e8")};
	background: ${(props) => (props.$active ? "#FFF0F6" : "#fff")};
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 22px;
	transition: all 0.15s ease;
	flex-shrink: 0;

	&:hover {
		border-color: #f72586;
		background: #fff0f6;
	}
`;

const MobileBreadcrumb = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 10px 16px;
	overflow-x: auto;
	white-space: nowrap;
	font-size: 13px;
	border-bottom: 1px solid #f0f0f0;

	&::-webkit-scrollbar {
		height: 0;
	}
`;

const BackButton = styled.button`
	background: none;
	border: none;
	cursor: pointer;
	color: #666;
	font-size: 16px;
	padding: 4px;
	flex-shrink: 0;
	display: flex;
	align-items: center;
`;

const MobileCard = styled.div`
	padding: 8px;
`;

const MobileBlockItem = styled.button<{ $active: boolean }>`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 12px 16px;
	margin-bottom: 4px;
	border-radius: 8px;
	border: none;
	text-align: left;
	font-size: 14px;
	font-weight: 400;
	height: 52px;
	min-height: 52px;
	max-height: 52px;
	cursor: pointer;
	transition: background 0.12s ease;

	background: ${(props) => (props.$active ? "#F72586" : "#f8f8f8")};
	color: ${(props) => (props.$active ? "#fff" : "#333")};

	&:hover {
		background: ${(props) => (props.$active ? "#e01a6f" : "#f0f0f0")};
	}
`;

// ─── Helper: get icon for block type ─────────────────────────────

const BLOCK_ICON_MAP: Partial<Record<BlockType, IconName>> = {
	[BlockType.CREATOR_FINDING]: "sourcing",
	[BlockType.CONTENT_EXECUTION]: "execution",
	[BlockType.CREATOR]: "creator",
	[BlockType.DELIVERABLE]: "folder",
	[BlockType.CONCEPT]: "concept",
	[BlockType.VIDEO]: "video",
	[BlockType.LINK]: "link",
};

// Block types that use a curved sub-arrow icon instead of a file icon
const SUB_ARROW_TYPES = new Set([BlockType.CREATOR_LIST, BlockType.CREATOR_OUTREACH, BlockType.CREATOR_GROUPING]);

const SubArrowIcon: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = "currentColor" }) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" style={{ display: "block" }}>
		<path
			d="M4 2v6c0 2.2 1.8 4 4 4h5"
			stroke={color}
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
		<path
			d="M10.5 9.5L13 12l-2.5 2.5"
			stroke={color}
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			fill="none"
		/>
	</svg>
);

// Legacy display name override: CREATOR_FINDING was renamed to "Creator Sourcing"
function getBlockDisplayName(block: BlockData): string {
	if (block.type === BlockType.CREATOR_FINDING && block.name === "Creator Finding") {
		return "Creator Sourcing";
	}
	return block.name;
}

function getBlockIcon(type: BlockType, selected: boolean): React.ReactNode {
	if (SUB_ARROW_TYPES.has(type)) {
		return <SubArrowIcon color={selected ? "#fff" : "currentColor"} />;
	}
	const name = BLOCK_ICON_MAP[type];
	if (!name) return null;
	return <Icon name={name} size={16} color={selected ? "#fff" : undefined} preserveOriginalColor={!selected} />;
}

// ─── Helper: build path from blockId ─────────────────────────────

function buildPathToBlock(
	blockId: string,
	_blocks: BlockData[],
	childrenOf: (id: string) => BlockData[],
	markets: BlockData[],
): string[] {
	for (const market of markets) {
		const path = dfs(market.id, blockId, childrenOf, new Set());
		if (path) return path;
	}
	return [];
}

function dfs(
	currentId: string,
	targetId: string,
	childrenOf: (id: string) => BlockData[],
	visited: Set<string>,
): string[] | null {
	if (currentId === targetId) return [currentId];
	if (visited.has(currentId)) return null;
	visited.add(currentId);

	for (const child of childrenOf(currentId)) {
		const result = dfs(child.id, targetId, childrenOf, visited);
		if (result) return [currentId, ...result];
	}
	return null;
}

// ─── Main Component ──────────────────────────────────────────────

export const VettingChain: React.FC<VettingChainProps> = ({
	blocks,
	onBlockClick,
	onAddNode,
	onMarkComplete,
	// onRename,
	onDelete,
	canManageDeliverables = false,
	verifiedDeliverableIds = new Set<string>(),
	immutableDeliverableIds = new Set<string>(),
	completionStatusResolved = true,
	mode = "edit",
}) => {
	const [searchParams] = useSearchParams();
	const selectedBlockIdFromUrl = searchParams.get("blockId");

	const { markets, childrenOf, blockById } = useBlockTree(blocks);

	// selectedPath: array of block IDs from market down to current selection
	const [selectedPath, setSelectedPath] = useState<string[]>([]);
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [contextMenu, setContextMenu] = useState<{ blockId: string; x: number; y: number } | null>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const sentinelRef = useRef<HTMLDivElement>(null);
	const columnsScrollRef = useRef<HTMLDivElement>(null);

	// ── Initialize path from URL blockId ──
	const initializedRef = useRef(false);
	useEffect(() => {
		if (selectedBlockIdFromUrl && markets.length > 0) {
			const path = buildPathToBlock(selectedBlockIdFromUrl, blocks, childrenOf, markets);
			if (path.length > 0) {
				setSelectedPath(path);
				initializedRef.current = true;
				return;
			}
		}
		if (markets.length > 0 && !initializedRef.current) {
			setSelectedPath((prev) => (prev.length === 0 ? [markets[0].id] : prev));
			initializedRef.current = true;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedBlockIdFromUrl, markets.length]);

	// ── Collapse/expand based on scroll direction ──
	// Collapse when scrolling down past sentinel. Expand when scrolling up.
	// After any state change, ignore scroll events for a lockout period to
	// prevent oscillation from the layout shift caused by expand/collapse.
	useEffect(() => {
		let lastScrollY = window.scrollY;
		let lockUntil = 0;

		const handleScroll = () => {
			const now = Date.now();
			if (now < lockUntil) {
				lastScrollY = window.scrollY;
				return;
			}

			const sentinel = sentinelRef.current;
			if (!sentinel) return;

			const currentScrollY = window.scrollY;
			const delta = currentScrollY - lastScrollY;
			const sentinelRect = sentinel.getBoundingClientRect();
			const pastSentinel = sentinelRect.bottom < 60;

			if (delta > 2 && pastSentinel) {
				setIsCollapsed(true);
				lockUntil = now + 500;
			} else if (delta < -2) {
				setIsCollapsed(false);
				lockUntil = now + 500;
			}

			lastScrollY = currentScrollY;
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	// ── Auto-scroll rightmost column into view ──
	useEffect(() => {
		const el = columnsScrollRef.current;
		if (el) {
			el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
		}
	}, [selectedPath.length]);

	// ── Derived: active market ──
	const activeMarketId = selectedPath.length > 0 ? selectedPath[0] : null;

	// The last block in the path that is the active content block (from URL)
	const activeContentBlockId = selectedBlockIdFromUrl;

	// ── Handlers ──
	const handleMarketSelect = useCallback((marketId: string) => {
		setSelectedPath([marketId]);
	}, []);

	const handleBlockSelect = useCallback(
		(blockId: string, columnIndex: number) => {
			const pathIndex = columnIndex + 1;
			const newPath = [...selectedPath.slice(0, pathIndex), blockId];
			setSelectedPath(newPath);

			if (mode === "view") {
				const block = blockById(blockId);
				if (block && CLICKABLE_TYPES.includes(block.type)) {
					onBlockClick?.(blockId);
				}
			}
		},
		[selectedPath, mode, blockById, onBlockClick],
	);

	const handleMobileBack = useCallback(() => {
		if (selectedPath.length > 1) {
			setSelectedPath((prev) => prev.slice(0, -1));
		}
	}, [selectedPath]);

	const handleMobileBlockTap = useCallback(
		(blockId: string) => {
			const block = blockById(blockId);
			if (!block) return;

			const children = childrenOf(blockId);
			if (children.length > 0) {
				setSelectedPath((prev) => [...prev, blockId]);
			}

			if (mode === "view" && CLICKABLE_TYPES.includes(block.type)) {
				onBlockClick?.(blockId);
			}
		},
		[blockById, childrenOf, mode, onBlockClick],
	);

	// ── Flatten same-column types (Creator List/Outreach/Grouping) ──
	const getColumnItems = useCallback(
		(parentId: string): BlockData[] => {
			const children = childrenOf(parentId);
			const result: BlockData[] = [];

			const collectSameColumnDescendants = (block: BlockData) => {
				result.push(block);
				if (SAME_COLUMN_TYPES.has(block.type)) {
					for (const child of childrenOf(block.id)) {
						if (SAME_COLUMN_TYPES.has(child.type)) {
							collectSameColumnDescendants(child);
						}
					}
				}
			};

			for (const child of children) {
				collectSameColumnDescendants(child);
			}
			return result;
		},
		[childrenOf],
	);

	// ── Build columns data ──
	const columns = useMemo(() => {
		if (selectedPath.length === 0) return [];

		const cols: { parentId: string; items: BlockData[]; selectedId?: string }[] = [];

		for (let i = 0; i < selectedPath.length; i++) {
			const blockId = selectedPath[i];
			const block = blockById(blockId);

			// Skip same-column types in the path — they're already merged into their parent column
			if (block && SAME_COLUMN_TYPES.has(block.type)) continue;

			const items = getColumnItems(blockId);
			const canAdd = block && ADDABLE_PARENT_TYPES.has(block.type);
			if (items.length === 0 && !canAdd && i > 0) break;

			cols.push({
				parentId: blockId,
				items,
				selectedId: selectedPath[i + 1],
			});
		}

		return cols;
	}, [selectedPath, blockById, getColumnItems]);

	// ── Breadcrumb names ──
	const breadcrumbItems = useMemo(() => {
		return selectedPath
			.map((id) => blockById(id))
			.filter((b): b is BlockData => !!b)
			.map((b) => ({
				id: b.id,
				name: getBlockDisplayName(b),
				flag: b.type === BlockType.MARKET ? getFlagUrl(b.name) : undefined,
				type: b.type,
			}));
	}, [selectedPath, blockById]);

	// ── Mobile: current level's children ──
	const mobileCurrentItems = useMemo(() => {
		if (selectedPath.length === 0) return [];
		const lastId = selectedPath[selectedPath.length - 1];
		return childrenOf(lastId);
	}, [selectedPath, childrenOf]);

	const expandFromMini = useCallback(() => {
		setIsCollapsed(false);
		sentinelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}, []);

	const contextBlock = contextMenu ? blockById(contextMenu.blockId) : undefined;
	const canToggleContextBlock =
		Boolean(onMarkComplete) &&
		Boolean(contextBlock) &&
		canToggleDeliverableCompletion({
			isComplete: contextBlock?.status === BlockStatus.COMPLETE,
			statusResolved: completionStatusResolved,
			isImmutable: contextBlock ? immutableDeliverableIds.has(contextBlock.id) : false,
		});
	const contextOptions = contextBlock
		? [
				...(canToggleContextBlock
					? [
							{
								value: "complete",
								label: (
									<ContextOption>
										<Icon name="check" size={20} color="#303030" />
										{contextBlock.status === BlockStatus.COMPLETE
											? "Mark as Incomplete"
											: "Mark as Complete"}
									</ContextOption>
								),
							},
						]
					: []),
				...(canManageDeliverables
					? [
							// Rename is intentionally hidden while CET-linked deliverables remain immutable.
							// {
							// 	value: "rename",
							// 	label: (
							// 		<ContextOption>
							// 			<Icon src="/icon/pencil-01.svg" size={20} preserveOriginalColor />
							// 			Rename
							// 		</ContextOption>
							// 	),
							// },
							// {
							// 	value: "delete",
							// 	label: (
							// 		<ContextOption $danger>
							// 			<Icon src="/icon/trash.svg" size={20} color="#d30024" />
							// 			Delete
							// 		</ContextOption>
							// 	),
							// },
						]
					: []),
			]
		: [];

	const runContextAction = (action: string) => {
		if (!contextMenu) return;
		const blockId = contextMenu.blockId;
		setContextMenu(null);
		const block = blockById(blockId);
		if (
			action === "complete" &&
			block &&
			canToggleDeliverableCompletion({
				isComplete: block.status === BlockStatus.COMPLETE,
				statusResolved: completionStatusResolved,
				isImmutable: immutableDeliverableIds.has(blockId),
			})
		) {
			onMarkComplete?.(blockId);
		}
		// Rename is intentionally hidden while CET-linked deliverables remain immutable.
		// if (action === "rename") onRename?.(blockId);
		if (action === "delete") onDelete?.(blockId);
	};

	// ── Render ──
	return (
		<>
			{contextMenu ? (
				<ContextAnchor $x={contextMenu.x} $y={contextMenu.y}>
					<AppSelect
						autoFocus
						open
						options={contextOptions}
						popupMatchSelectWidth={260}
						onChange={runContextAction}
						onOpenChange={(open) => {
							if (!open) setContextMenu(null);
						}}
					/>
				</ContextAnchor>
			) : null}
			<ScrollSentinel ref={sentinelRef} />
			<Wrapper $collapsed={isCollapsed}>
				{/* Collapsed breadcrumb bar — overlays on top when scrolled past */}
				<CollapsedWrap $visible={isCollapsed}>
					<CollapsedBar onClick={expandFromMini}>
						{breadcrumbItems.map((item, i) => (
							<React.Fragment key={item.id}>
								{i > 0 && <BreadcrumbSep>&gt;</BreadcrumbSep>}
								<BreadcrumbSegment $isLast={i === breadcrumbItems.length - 1}>
									{item.flag ? (
										<MarketFlag src={item.flag} alt="" />
									) : SUB_ARROW_TYPES.has(item.type as BlockType) ? (
										<SubArrowIcon size={14} />
									) : (
										<BreadcrumbIcon
											name={BLOCK_ICON_MAP[item.type as BlockType] || "folder"}
											size={14}
											preserveOriginalColor
											$isLast={i === breadcrumbItems.length - 1}
										/>
									)}
									{item.name}
								</BreadcrumbSegment>
							</React.Fragment>
						))}
					</CollapsedBar>
				</CollapsedWrap>

				<FullContainer ref={containerRef} $collapsed={isCollapsed}>
					{/* Desktop layout — collapses via CSS when scrolled past */}
					<ChainLayout $collapsed={isCollapsed} $height={mode === "edit" ? 420 : 310}>
						{/* Market pills — rectangular: flag + name + > */}
						<MarketColumn>
							{markets.map((market) => (
								<MarketPill
									key={market.id}
									$active={market.id === activeMarketId}
									onClick={() => handleMarketSelect(market.id)}
									title={market.name}
								>
									<MarketFlag src={getFlagUrl(market.name)} alt={getCountryShort(market.name)} />
									{market.name.length > 3
										? market.name.substring(0, 3).toUpperCase()
										: market.name.toUpperCase()}
									<MarketChevron />
								</MarketPill>
							))}
						</MarketColumn>

						{/* Block columns + breadcrumb */}
						<ColumnsSection>
							<ColumnsScroll ref={columnsScrollRef}>
								{columns.map((col, colIndex) => (
									<Column key={`${col.parentId}-${colIndex}`}>
										{col.items.map((block) => {
											const isInPath = block.id === col.selectedId;
											const isLeafActive = block.id === activeContentBlockId;
											const isMergedType = SAME_COLUMN_TYPES.has(block.type);
											const hasChildren = !isMergedType && childrenOf(block.id).length > 0;
											const isClickable =
												mode === "view"
													? CLICKABLE_TYPES.includes(block.type) || hasChildren
													: hasChildren;
											const icon = getBlockIcon(block.type, isLeafActive);
											const isComplete = block.status === BlockStatus.COMPLETE;
											const canToggleCompletion = canToggleDeliverableCompletion({
												isComplete,
												statusResolved: completionStatusResolved,
												isImmutable: immutableDeliverableIds.has(block.id),
											});
											const isVerified = verifiedDeliverableIds.has(block.id);
											const showCompletedTick =
												isVerified || (canManageDeliverables && isComplete);

											const menuItems: MenuProps["items"] = [
												// Only the assigned creator can complete a deliverable (server-enforced);
												// offering this here would queue a mark_complete op the backend rejects
												// inside the batch transaction, discarding the rest of the buffered save.
												...(block.type === BlockType.DELIVERABLE
													? []
													: [
															{
																key: "complete",
																label: isComplete
																	? "Mark as Incomplete"
																	: "Mark as Completed",
																disabled: !canToggleCompletion,
																onClick: (e: {
																	domEvent: { stopPropagation: () => void };
																}) => {
																	e.domEvent.stopPropagation();
																	if (canToggleCompletion) onMarkComplete?.(block.id);
																},
															},
														]),
												// Rename is intentionally hidden while CET-linked deliverables remain immutable.
												// {
												// 	key: "rename",
												// 	label: "Rename",
												// 	onClick: (e) => {
												// 		e.domEvent.stopPropagation();
												// 		onRename?.(block.id);
												// 	},
												// },
												{
													key: "delete",
													label: "Delete",
													danger: true,
													onClick: (e) => {
														e.domEvent.stopPropagation();
														onDelete?.(block.id);
													},
												},
											];

											return (
												<BlockCard
													key={block.id}
													$inPath={isInPath}
													$isLeafActive={isLeafActive}
													$clickable={isClickable || mode === "edit"}
													onClick={() => isClickable && handleBlockSelect(block.id, colIndex)}
													onContextMenu={(event) => {
														if (
															mode !== "view" ||
															block.type !== BlockType.DELIVERABLE ||
															!onMarkComplete ||
															!canToggleCompletion
														)
															return;
														event.preventDefault();
														event.stopPropagation();
														setContextMenu({
															blockId: block.id,
															x: event.clientX,
															y: event.clientY,
														});
													}}
												>
													<BlockText>
														<BlockTitleRow>
															{icon && (
																<BlockIcon $light={isLeafActive}>{icon}</BlockIcon>
															)}
															<BlockName $emphasized={isInPath || isLeafActive}>
																{getBlockDisplayName(block)}
															</BlockName>
														</BlockTitleRow>
														{isComplete && !showCompletedTick ? (
															<BlockStatusText $selected={isLeafActive}>
																Marked as Complete
															</BlockStatusText>
														) : null}
													</BlockText>
													{showCompletedTick ? (
														<VerifiedMark>
															<Icon name="check" size={12} color="#4d9e20" />
														</VerifiedMark>
													) : null}
													{mode === "edit" && EDITABLE_TYPES.has(block.type) ? (
														<TrailingAction>
															{hasChildren && (
																<Chevron
																	className="trailing-chevron"
																	$light={isLeafActive}
																/>
															)}
															<Dropdown
																menu={{ items: menuItems }}
																trigger={["click"]}
																placement="bottomLeft"
															>
																<MoreDots
																	className="trailing-more"
																	$light={isLeafActive}
																	onClick={(e) => e.stopPropagation()}
																/>
															</Dropdown>
														</TrailingAction>
													) : mode === "view" &&
													  canManageDeliverables &&
													  onMarkComplete &&
													  block.type === BlockType.DELIVERABLE ? (
														<TrailingAction>
															<MoreDots
																className="trailing-more"
																$light={isLeafActive}
																onClick={(event) => {
																	event.stopPropagation();
																	const rect =
																		event.currentTarget.getBoundingClientRect();
																	setContextMenu({
																		blockId: block.id,
																		x: rect.left,
																		y: rect.bottom,
																	});
																}}
															/>
														</TrailingAction>
													) : (
														hasChildren && <Chevron $light={isLeafActive} />
													)}
												</BlockCard>
											);
										})}
										{(() => {
											const parentBlock = blockById(col.parentId);
											const canAdd = parentBlock && ADDABLE_PARENT_TYPES.has(parentBlock.type);
											return canAdd && mode !== "view" ? (
												<AddBtn onClick={() => onAddNode?.(col.parentId)}>
													<PlusOutlined style={{ fontSize: 10 }} />
													Add
												</AddBtn>
											) : null;
										})()}
									</Column>
								))}
							</ColumnsScroll>
							{/* Breadcrumb bar — aligned with folders */}
							{breadcrumbItems.length > 0 && (
								<BreadcrumbBar>
									{breadcrumbItems.map((item, i) => (
										<React.Fragment key={item.id}>
											{i > 0 && <BreadcrumbSep>&gt;</BreadcrumbSep>}
											<BreadcrumbSegment $isLast={i === breadcrumbItems.length - 1}>
												{item.flag ? (
													<MarketFlag src={item.flag} alt="" />
												) : SUB_ARROW_TYPES.has(item.type as BlockType) ? (
													<SubArrowIcon size={14} />
												) : (
													<BreadcrumbIcon
														name={BLOCK_ICON_MAP[item.type as BlockType] || "folder"}
														size={14}
														preserveOriginalColor
														$isLast={i === breadcrumbItems.length - 1}
													/>
												)}
												{item.name}
											</BreadcrumbSegment>
										</React.Fragment>
									))}
								</BreadcrumbBar>
							)}
						</ColumnsSection>
					</ChainLayout>

					{/* Mobile layout */}
					<MobileContainer>
						<MobileMarketRow>
							{markets.map((market) => (
								<MobileMarketPill
									key={market.id}
									$active={market.id === activeMarketId}
									onClick={() => handleMarketSelect(market.id)}
									title={market.name}
								>
									<MarketFlag src={getFlagUrl(market.name)} alt={getCountryShort(market.name)} />
								</MobileMarketPill>
							))}
						</MobileMarketRow>

						{selectedPath.length > 1 && (
							<MobileBreadcrumb>
								<BackButton onClick={handleMobileBack}>
									<LeftOutlined />
								</BackButton>
								{breadcrumbItems.slice(1).map((item, i, arr) => (
									<React.Fragment key={item.id}>
										{i > 0 && <BreadcrumbSep>&gt;</BreadcrumbSep>}
										<BreadcrumbSegment $isLast={i === arr.length - 1}>
											{item.name}
										</BreadcrumbSegment>
									</React.Fragment>
								))}
							</MobileBreadcrumb>
						)}

						<MobileCard>
							{mobileCurrentItems.length === 0 ? (
								<div style={{ padding: "16px", color: "#999", textAlign: "center", fontSize: "14px" }}>
									{selectedPath.length === 0 ? "Select a market" : "No items"}
								</div>
							) : (
								mobileCurrentItems.map((block) => {
									const isActive = block.id === activeContentBlockId;
									return (
										<MobileBlockItem
											key={block.id}
											$active={isActive}
											onClick={() => handleMobileBlockTap(block.id)}
										>
											<BlockName $emphasized={isActive}>{getBlockDisplayName(block)}</BlockName>
											{childrenOf(block.id).length > 0 && <Chevron $light={isActive} />}
										</MobileBlockItem>
									);
								})
							)}
						</MobileCard>
					</MobileContainer>
				</FullContainer>
			</Wrapper>
		</>
	);
};
